import { Reference, useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import Form from "@components/forms/Form";
import TinyText from "@components/text/TinyText";
import ExternalLink from "@components/typography/ExternalLink";
import {
  FaxAttemptFragmentDoc,
  SignedDocumentFragment as SignedDocument,
  SignedDocumentFaxDocument,
  SignedDocumentFaxMutation,
  SignedDocumentFaxMutationVariables
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";

type FormData = {
  documentFor: string;
  faxFor: string;
  faxNumber: string;
  note: string;
};

type Props = {
  autofocus: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  signedDocument: SignedDocument;
};

const SignedDocumentFaxForm = ({ autofocus, onCancel, onSuccess, signedDocument }: Props) => {
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [signedDocumentFax] = useMutation<SignedDocumentFaxMutation, SignedDocumentFaxMutationVariables>(
    SignedDocumentFaxDocument,
    {
      update: (store, { data }) => {
        if (data?.signedDocumentFax?.faxAttempt) {
          const newFaxAttempt = data.signedDocumentFax.faxAttempt;
          store.modify({
            fields: {
              faxAttempts(cachedFaxAttemptRefs: readonly Reference[]) {
                const newFaxAttemptRef = store.writeFragment({
                  data: newFaxAttempt,
                  fragment: FaxAttemptFragmentDoc,
                  fragmentName: "FaxAttempt"
                });
                if (newFaxAttemptRef) {
                  return [...cachedFaxAttemptRefs, newFaxAttemptRef];
                }
                return [...cachedFaxAttemptRefs];
              }
            },
            id: store.identify({ ...signedDocument })
          });
        }
      }
    }
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      documentFor: yup.string().required(intl.formatMessage({ id: "errors.signedDocumentFax.documentFor.blank" })),
      faxFor: yup.string().required(intl.formatMessage({ id: "errors.signedDocumentFax.faxFor.blank" })),
      faxNumber: yup
        .string()
        .matches(/^\d+$/, intl.formatMessage({ id: "errors.signedDocumentFax.faxNumber.onlyNumbers" }))
        .required(intl.formatMessage({ id: "errors.signedDocumentFax.faxNumber.blank" })),
      note: yup.string().required(intl.formatMessage({ id: "errors.signedDocumentFax.note.blank" }))
    })
    .required();

  const defaultValues: FormData = {
    documentFor: signedDocument.documentFor ?? "",
    faxFor: "",
    faxNumber: "",
    note: ""
  };

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return signedDocumentFax({
      variables: { ...form.getValues(), id: signedDocument.id }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.signedDocumentFax,
        intl,
        onSuccess: () => {
          if (response.data?.signedDocumentFax?.faxAttempt) {
            onSuccess();
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.clientAccountUpdate"
      });
    });
  }, [form, intl, onSuccess, signedDocument.id, signedDocumentFax]);

  return (
    <Form data-test="account-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input<FormData>
        autofocus={autofocus}
        error={form.formState.errors.documentFor}
        label={intl.formatMessage({ id: "forms.signedDocumentFax.documentForLabel" })}
        register={form.register("documentFor")}
        required
      />
      <Input<FormData>
        error={form.formState.errors.faxFor}
        label={intl.formatMessage({ id: "forms.signedDocumentFax.faxForLabel" })}
        register={form.register("faxFor")}
        required
      />
      <Input<FormData>
        error={form.formState.errors.faxNumber}
        label={intl.formatMessage({ id: "forms.signedDocumentFax.faxNumberLabel" })}
        register={form.register("faxNumber")}
        required
      />
      <TinyText>
        <FormattedMessage
          id="forms.signedDocumentFax.internationalNumbers"
          values={{
            this: (
              <ExternalLink href="https://app.hellofax.com/info/internationalFaxPricing" linkEventCreateVariables={{}}>
                {intl.formatMessage({ id: "dictionary.this" })}
              </ExternalLink>
            )
          }}
        />
      </TinyText>
      <Input
        error={form.formState.errors.note}
        label={intl.formatMessage({ id: "forms.signedDocumentFax.noteLabel" })}
        register={form.register("note")}
        required
      />
      <FormButtons hookForm={form} onCancel={onCancel} saveKey="forms.signedDocumentFax.sendFax" />
    </Form>
  );
};

export default SignedDocumentFaxForm;
